import { ExternalLink, FileText } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/badge';
import type {
  DiscriminatedTreatmentRequestUpdatedEvent,
  DiscriminatedTreatmentRequestUpdatedEventV1,
} from '@/services/api/generated/taskActivityService';

import {
  EventActions,
  EventBody,
  EventContainer,
  EventHeader,
} from './EventPrimitives';

interface TreatmentRequestUpdatedEventProps {
  event:
    | DiscriminatedTreatmentRequestUpdatedEvent
    | DiscriminatedTreatmentRequestUpdatedEventV1;
  matterId: string;
}

export const TreatmentRequestUpdatedEvent: React.FC<
  TreatmentRequestUpdatedEventProps
> = ({ event, matterId }) => {
  const { payload, occurred_at, user, agent, version } = event;

  // Handle both v1 and v2 versions
  const isV2 = version === 2 && 'treatment_request' in payload;
  const treatmentRequest = isV2 ? payload.treatment_request : null;
  const previous = isV2 ? payload.previous : null;
  const { diff } = payload;

  // Check what changed
  const statusChanged =
    diff.status !== undefined && (!previous || diff.status !== previous.status);
  const actorChanged =
    diff.actor !== undefined && (!previous || diff.actor !== previous.actor);
  const certifiedChanged =
    diff.is_certified !== undefined &&
    (!previous || diff.is_certified !== previous.is_certified);
  const urgentChanged =
    diff.is_urgent !== undefined &&
    (!previous || diff.is_urgent !== previous.is_urgent);
  const providedRecordsChanged =
    diff.provided_records !== undefined &&
    (!previous ||
      JSON.stringify(diff.provided_records) !==
        JSON.stringify(previous.provided_records));

  return (
    <EventContainer>
      <EventHeader
        icon={<FileText size={20} />}
        title="Treatment Request Updated"
        timestamp={occurred_at}
        actor={user}
        agent={agent}
      />

      <EventBody>
        <div className="space-y-1.5">
          {/* All changes on one line */}
          <div className="flex items-center gap-2 flex-wrap text-xs">
            {/* Status Change */}
            {statusChanged && diff.status ? (
              <>
                {previous?.status && (
                  <>
                    <Badge variant="outline" className="line-through">
                      {formatStatus(previous.status)}
                    </Badge>
                    <span className="text-slate-400">→</span>
                  </>
                )}
                <Badge variant={getStatusVariant(diff.status)}>
                  {formatStatus(diff.status)}
                </Badge>
                {(actorChanged ||
                  certifiedChanged ||
                  urgentChanged ||
                  providedRecordsChanged) && (
                  <span className="text-slate-400">•</span>
                )}
              </>
            ) : (
              /* Current Status (if available from v2 and no status changed) */
              isV2 &&
              treatmentRequest && (
                <>
                  <Badge
                    variant={getStatusVariant(
                      treatmentRequest.status || 'unknown',
                    )}
                  >
                    {formatStatus(treatmentRequest.status || 'unknown')}
                  </Badge>
                  {(actorChanged ||
                    certifiedChanged ||
                    urgentChanged ||
                    providedRecordsChanged) && (
                    <span className="text-slate-400">•</span>
                  )}
                </>
              )
            )}

            {/* Actor Change */}
            {actorChanged && diff.actor && (
              <>
                {previous?.actor && (
                  <>
                    <Badge variant="outline" className="line-through">
                      {formatActor(previous.actor)}
                    </Badge>
                    <span className="text-slate-400">→</span>
                  </>
                )}
                <Badge variant="outline">{formatActor(diff.actor)}</Badge>
                {(certifiedChanged ||
                  urgentChanged ||
                  providedRecordsChanged) && (
                  <span className="text-slate-400">•</span>
                )}
              </>
            )}

            {/* Provided Records Change */}
            {providedRecordsChanged && diff.provided_records && (
              <>
                {previous?.provided_records && (
                  <>
                    <span className="line-through text-slate-500 text-[11px]">
                      {previous.provided_records.join(', ')}
                    </span>
                    <span className="text-slate-400">→</span>
                  </>
                )}
                <span className="text-green-700 text-[11px]">
                  {diff.provided_records.join(', ')}
                </span>
                {(certifiedChanged || urgentChanged) && (
                  <span className="text-slate-400">•</span>
                )}
              </>
            )}

            {/* Certified Flag Change */}
            {certifiedChanged && (
              <>
                {previous && previous.is_certified !== undefined && (
                  <>
                    <Badge variant="outline" className="line-through">
                      {previous.is_certified ? 'Certified' : 'Not Certified'}
                    </Badge>
                    <span className="text-slate-400">→</span>
                  </>
                )}
                <Badge variant={diff.is_certified ? 'default' : 'outline'}>
                  {diff.is_certified ? 'Certified' : 'Not Certified'}
                </Badge>
                {urgentChanged && <span className="text-slate-400">•</span>}
              </>
            )}

            {/* Urgent Flag Change */}
            {urgentChanged && (
              <>
                {previous && previous.is_urgent !== undefined && (
                  <>
                    <Badge variant="outline" className="line-through">
                      {previous.is_urgent ? 'Urgent' : 'Not Urgent'}
                    </Badge>
                    <span className="text-slate-400">→</span>
                  </>
                )}
                <Badge variant={diff.is_urgent ? 'destructive' : 'outline'}>
                  {diff.is_urgent ? 'Urgent' : 'Not Urgent'}
                </Badge>
              </>
            )}
          </div>
        </div>
      </EventBody>

      <EventActions>
        {isV2 && treatmentRequest ? (
          <a
            href={`/matters/${matterId}/treatment?request_id=${treatmentRequest.id}&provider_id=${treatmentRequest.provider_id}`}
            className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
          >
            View Treatment Request
            <ExternalLink size={11} />
          </a>
        ) : (
          <a
            href={`/matters/${matterId}/treatment`}
            className="text-[11px] text-blue-600 hover:text-blue-800 flex items-center gap-1 hover:underline"
          >
            View Treatment Requests
            <ExternalLink size={11} />
          </a>
        )}
      </EventActions>
    </EventContainer>
  );
};

// Helper functions for formatting
function formatStatus(status: string): string {
  const statuses: Record<string, string> = {
    not_ready: 'Not Ready',
    pending: 'Pending',
    request_submitted: 'Request Submitted',
    request_received: 'Request Received',
    awaiting_invoice: 'Awaiting Invoice',
    pending_payment: 'Pending Payment',
    records_pending: 'Records Pending',
    records_delivered: 'Records Delivered',
    records_ingested: 'Records Ingested',
    rejected: 'Rejected',
    cancelled: 'Cancelled',
  };
  return statuses[status] || status;
}

function getStatusVariant(
  status: string,
): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'records_delivered':
    case 'records_ingested':
      return 'default'; // Green
    case 'pending':
    case 'request_submitted':
    case 'request_received':
    case 'records_pending':
      return 'secondary'; // Blue
    case 'rejected':
    case 'cancelled':
      return 'destructive'; // Red
    case 'not_ready':
    case 'awaiting_invoice':
    case 'pending_payment':
      return 'outline'; // Gray
    default:
      return 'outline';
  }
}

function formatActor(actor: string): string {
  const actors: Record<string, string> = {
    user: 'User',
    agent: 'Agent',
  };
  return actors[actor] || actor;
}
